import type { DashboardBuilderState, DashboardWidgetInstance } from './types';
import { DASHBOARD_BUILDER_SCHEMA_VERSION } from './types';
import { DEFAULT_DASHBOARD_HEADER_CHROME, DEFAULT_HEADER_WIDGETS } from './dashboardHeaderDefaults';

export interface DashboardTemplate {
  id: string;
  name: string;
  description: string;
  category: 'general' | 'operations' | 'analytics';
  /** Grid cells as [x, y, w, h] for the mini-preview thumbnail */
  previewCells: Array<[number, number, number, number]>;
  build: () => DashboardBuilderState;
}

let _counter = 0;
function wid(): string {
  return `t${++_counter}`;
}

function base(
  layout: DashboardBuilderState['layout'],
  widgets: Record<string, DashboardWidgetInstance>,
  opts?: { layoutMode?: 'fit' | 'scroll' },
): DashboardBuilderState {
  _counter = 0;
  return {
    version: DASHBOARD_BUILDER_SCHEMA_VERSION,
    header: { ...DEFAULT_DASHBOARD_HEADER_CHROME },
    headerWidgets: [...DEFAULT_HEADER_WIDGETS],
    layout,
    widgets,
    layoutMode: opts?.layoutMode ?? 'fit',
  };
}

const TEMPLATES: DashboardTemplate[] = [
  {
    id: 'blank',
    name: 'Blank',
    description: 'Empty canvas — freestyle layout',
    category: 'general',
    previewCells: [[0, 0, 6, 6], [6, 0, 6, 6]],
    build: () => {
      const w1 = wid(), w2 = wid();
      return base(
        [
          { i: w1, x: 0, y: 0, w: 6, h: 6, minW: 2, minH: 2 },
          { i: w2, x: 6, y: 0, w: 6, h: 6, minW: 2, minH: 2 },
        ],
        {
          [w1]: { type: 'placeholder' },
          [w2]: { type: 'text', config: { title: 'Getting started', body: 'Turn on Edit to drag, resize, add widgets, or apply a different template.' } },
        },
        { layoutMode: 'scroll' },
      );
    },
  },
  {
    id: 'overview',
    name: 'Overview',
    description: 'Large map with indicators and geofence panel',
    category: 'operations',
    previewCells: [[0, 0, 8, 10], [8, 0, 4, 3], [8, 3, 4, 3], [8, 6, 4, 4]],
    build: () => {
      const map = wid(), ind1 = wid(), ind2 = wid(), geo = wid();
      return base(
        [
          { i: map, x: 0, y: 0, w: 8, h: 10, minW: 4, minH: 6 },
          { i: ind1, x: 8, y: 0, w: 4, h: 3, minW: 2, minH: 2 },
          { i: ind2, x: 8, y: 3, w: 4, h: 3, minW: 2, minH: 2 },
          { i: geo, x: 8, y: 6, w: 4, h: 4, minW: 3, minH: 3 },
        ],
        {
          [map]: { type: 'web_map', config: { webMapOptions: { layers: [], basemap: 'dark' } } },
          [ind1]: { type: 'indicator', config: { customTitle: 'Total Records' } },
          [ind2]: { type: 'indicator', config: { customTitle: 'Active Devices' } },
          [geo]: { type: 'geofence', config: { geofences: [], basemap: 'dark' } },
        },
      );
    },
  },
  {
    id: 'analytics',
    name: 'Analytics',
    description: 'Chart grid with indicators and data table',
    category: 'analytics',
    previewCells: [[0, 0, 3, 3], [3, 0, 3, 3], [6, 0, 3, 3], [9, 0, 3, 3], [0, 3, 6, 5], [6, 3, 6, 5], [0, 8, 12, 4]],
    build: () => {
      const i1 = wid(), i2 = wid(), i3 = wid(), i4 = wid();
      const c1 = wid(), c2 = wid(), tbl = wid();
      return base(
        [
          { i: i1, x: 0, y: 0, w: 3, h: 3, minW: 2, minH: 2 },
          { i: i2, x: 3, y: 0, w: 3, h: 3, minW: 2, minH: 2 },
          { i: i3, x: 6, y: 0, w: 3, h: 3, minW: 2, minH: 2 },
          { i: i4, x: 9, y: 0, w: 3, h: 3, minW: 2, minH: 2 },
          { i: c1, x: 0, y: 3, w: 6, h: 5, minW: 3, minH: 4 },
          { i: c2, x: 6, y: 3, w: 6, h: 5, minW: 3, minH: 4 },
          { i: tbl, x: 0, y: 8, w: 12, h: 4, minW: 4, minH: 3 },
        ],
        {
          [i1]: { type: 'indicator', config: { customTitle: 'Metric 1' } },
          [i2]: { type: 'indicator', config: { customTitle: 'Metric 2' } },
          [i3]: { type: 'indicator', config: { customTitle: 'Metric 3' } },
          [i4]: { type: 'indicator', config: { customTitle: 'Metric 4' } },
          [c1]: { type: 'chart_bar', config: { customTitle: 'Bar Chart' } },
          [c2]: { type: 'chart_donut', config: { customTitle: 'Distribution' } },
          [tbl]: { type: 'data_table', config: { customTitle: 'Data Table' } },
        },
      );
    },
  },
  {
    id: 'cop',
    name: 'COP / Monitoring',
    description: 'Full-width map with geofences and analytics',
    category: 'operations',
    previewCells: [[0, 0, 9, 8], [9, 0, 3, 8], [0, 8, 6, 4], [6, 8, 6, 4]],
    build: () => {
      const map = wid(), geo = wid(), sa = wid(), chart = wid();
      return base(
        [
          { i: map, x: 0, y: 0, w: 9, h: 8, minW: 4, minH: 6 },
          { i: geo, x: 9, y: 0, w: 3, h: 8, minW: 3, minH: 5 },
          { i: sa, x: 0, y: 8, w: 6, h: 4, minW: 3, minH: 3 },
          { i: chart, x: 6, y: 8, w: 6, h: 4, minW: 3, minH: 3 },
        ],
        {
          [map]: { type: 'web_map', config: { webMapOptions: { layers: [], basemap: 'dark' } } },
          [geo]: { type: 'geofence', config: { geofences: [], basemap: 'dark' } },
          [sa]: { type: 'spatial_analytics', config: { dataBinding: {}, spatialInstructions: '' } },
          [chart]: { type: 'chart_line', config: { customTitle: 'Timeline' } },
        },
      );
    },
  },
  {
    id: 'briefing',
    name: 'Briefing',
    description: 'Text-heavy layout for reports and presentations',
    category: 'general',
    previewCells: [[0, 0, 12, 2], [0, 2, 6, 5], [6, 2, 6, 5], [0, 7, 4, 5], [4, 7, 4, 5], [8, 7, 4, 5]],
    build: () => {
      const hdr = wid(), left = wid(), right = wid();
      const c1 = wid(), c2 = wid(), c3 = wid();
      return base(
        [
          { i: hdr, x: 0, y: 0, w: 12, h: 2, minW: 6, minH: 2 },
          { i: left, x: 0, y: 2, w: 6, h: 5, minW: 3, minH: 3 },
          { i: right, x: 6, y: 2, w: 6, h: 5, minW: 3, minH: 3 },
          { i: c1, x: 0, y: 7, w: 4, h: 5, minW: 3, minH: 3 },
          { i: c2, x: 4, y: 7, w: 4, h: 5, minW: 3, minH: 3 },
          { i: c3, x: 8, y: 7, w: 4, h: 5, minW: 3, minH: 3 },
        ],
        {
          [hdr]: { type: 'text', config: { title: 'Briefing Title', body: 'Executive summary and key takeaways.' } },
          [left]: { type: 'text', config: { title: 'Section 1', body: 'Detailed narrative content for the left column.' } },
          [right]: { type: 'text', config: { title: 'Section 2', body: 'Detailed narrative content for the right column.' } },
          [c1]: { type: 'chart_bar', config: { customTitle: 'Chart 1' } },
          [c2]: { type: 'chart_line', config: { customTitle: 'Chart 2' } },
          [c3]: { type: 'chart_donut', config: { customTitle: 'Chart 3' } },
        },
      );
    },
  },
];

export function getDashboardTemplates(): DashboardTemplate[] {
  return TEMPLATES;
}

export function getDashboardTemplate(id: string): DashboardTemplate | undefined {
  return TEMPLATES.find((t) => t.id === id);
}
